// @ts-check

/**
 * @file Configures Velocious transport and frontend model registration.
 */

import FrontendModelBase from "velocious/build/src/frontend-models/base.js"

import {APP_FRONTEND_REQUEST_HEADER, APP_FRONTEND_REQUEST_VALUE} from "../../../shared/app-frontend-request.js"
import {resolveApiBaseUrl, resolveWebsocketUrl} from "../shared/backend-api"
import {expoPublicEnvFlag} from "../shared/expo-public-env"
import {getSessionTokenSync, SESSION_TOKEN_HEADER} from "../shared/session-option"
import {installOfflineSyncProvider} from "../sync/offline-sync-provider"
import "../frontend-models/board.js"
import "../frontend-models/board-column.js"
import "../frontend-models/task-board.js"

// Import all frontend models to trigger registration
import "../frontend-models/setup.js"

const shared = {
  initialized: false,
  transportParams: /** @type {Record<string, string>} */ ({})
}
const TRANSPORT_PARAM_NAMES = ["backendHost", "backendPort", "backendProtocol"]
const loggedSystemTestTransportUrls = new Set()

/**
 * Logs native test transport URLs once so failed APK runs include the resolved target.
 * @param {string} type Transport type being configured.
 * @param {string} url Resolved transport URL.
 * @returns {void} Nothing.
 */
function logSystemTestTransportUrl(type, url) {
  if (!expoPublicEnvFlag("EXPO_PUBLIC_SYSTEM_TEST")) return
  const key = `${type}:${url}`
  if (loggedSystemTestTransportUrls.has(key)) return
  loggedSystemTestTransportUrls.add(key)
  console.log("System-test transport URL:", type, url)
}

/**
 * Reads backend transport params from the live URL and remembers them
 * for later normal in-app navigation that does not carry test params.
 * @returns {Record<string, string>} Query params from the current runtime URL.
 */
function paramsFromWindowLocation() {
  if (typeof window === "undefined" || !window.location) {
    return shared.transportParams
  }

  const currentParams = Object.fromEntries(new URLSearchParams(window.location.search).entries())

  for (const paramName of TRANSPORT_PARAM_NAMES) {
    if (typeof currentParams[paramName] === "string" && currentParams[paramName]) {
      shared.transportParams[paramName] = currentParams[paramName]
    }
  }

  return {
    ...shared.transportParams,
    ...currentParams
  }
}

/**
 * Installs transport configuration and model classes once.
 * @returns {void} Nothing.
 */
export default function initializeVelocious() {
  if (shared.initialized) return

  FrontendModelBase.configureTransport({
    requestHeaders: () => {
      const token = getSessionTokenSync()
      const headers = {[APP_FRONTEND_REQUEST_HEADER]: APP_FRONTEND_REQUEST_VALUE}

      if (token) headers[SESSION_TOKEN_HEADER] = token

      return headers
    },
    shared: true,
    url: () => {
      const url = resolveApiBaseUrl(paramsFromWindowLocation())
      logSystemTestTransportUrl("http", url)
      return url
    },
    websocketUrl: () => {
      const url = resolveWebsocketUrl(paramsFromWindowLocation())
      logSystemTestTransportUrl("websocket", url)
      return url
    }
  })
  FrontendModelBase.installWebsocketTestHooks()
  installOfflineSyncProvider()

  shared.initialized = true
}

/**
 * Resets Velocious transport state so the next test re-initializes.
 * @returns {Promise<void>} Resolves after transport requests finish and the websocket is disconnected.
 */
export async function teardownVelocious() {
  await FrontendModelBase.waitForIdle({quietMs: 50})
  await FrontendModelBase.disconnectWebsocket()
  shared.initialized = false
}
